import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import { StatCard } from "@/components/ui/stat-card";
import { CardStatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { patronsApi, type PatronListItem } from "@/lib/api/patrons";
import { formatPersonName } from "@/lib/person-name";
import { usePatronTypes } from "@/hooks/usePatronTypes";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import type { Branch } from "@/lib/api/branches";
import {
  Users,
  Eye,
  Edit2,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  ShieldOff,
  MoreVertical,
  User,
} from "lucide-react";
import { isGlobalSuperAdmin } from "@/lib/permissions";

const PAGE_SIZE = 20;

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

function PatronRowActionsMenu({
  patron,
  hasEditPermission,
  onEdit,
  onManage,
  onDesk,
}: {
  patron: PatronListItem;
  hasEditPermission: boolean;
  onEdit: () => void;
  onManage: () => void;
  onDesk: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; openUp: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 208;
    const estimatedHeight = hasEditPermission ? 140 : 100;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight + 12;
    const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
    setCoords({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left,
      openUp,
    });
  }, [hasEditPermission]);

  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    placeMenu();
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, placeMenu]);

  const menu = open && coords && (
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: coords.openUp ? "translateY(-100%)" : undefined,
      }}
      className="z-[200] w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-lg"
    >
      {hasEditPermission && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
          onClick={() => {
            setOpen(false);
            onEdit();
          }}
        >
          <Edit2 className="h-4 w-4 shrink-0 text-slate-500" />
          Edit
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
        onClick={() => {
          setOpen(false);
          onManage();
        }}
      >
        <Eye className="h-4 w-4 shrink-0 text-slate-500" />
        Manage
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
        onClick={() => {
          setOpen(false);
          onDesk();
        }}
      >
        <User className="h-4 w-4 shrink-0 text-slate-500" />
        Desk
      </button>
    </div>
  );

  return (
    <div className="relative flex justify-end">
      <Button
        ref={btnRef}
        type="button"
        variant="ghost"
        size="icon"
        className="shrink-0 text-slate-600 hover:text-slate-900 touch-manipulation"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Actions for ${patron.email}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreVertical className="h-4 w-4" />
      </Button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

const PatronsListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [patrons, setPatrons] = useState<PatronListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [patronTypeFilter, setPatronTypeFilter] = useState<string>("all");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<"joinedAt" | "email" | "name">("joinedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [editPatron, setEditPatron] = useState<PatronListItem | null>(null);
  const [editPatronTypeId, setEditPatronTypeId] = useState("");
  const [editHomeBranchId, setEditHomeBranchId] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const userPermissions = user?.permissions || [];
  const hasPermission =
    isGlobalSuperAdmin(user) ||
    userPermissions.some(
      (p: string) => p === "patron.list" || p === "patron.view" || p === "patron.*" || p === "*"
    );
  const hasEditPermission =
    isGlobalSuperAdmin(user) ||
    userPermissions.some((p: string) => p === "patron.edit" || p === "patron.*" || p === "*");

  const { data: patronTypes = [] } = usePatronTypes(true);
  const { data: branches = [] } = useActiveBranches({ enabled: hasPermission });

  const loadPatrons = useCallback(async () => {
    try {
      setLoading(true);
      const result = await patronsApi.list({
        q: searchQuery || undefined,
        patronTypeId: patronTypeFilter !== "all" ? patronTypeFilter : undefined,
        homeBranchId: branchFilter !== "all" ? branchFilter : undefined,
        page: currentPage,
        limit: PAGE_SIZE,
        sortBy,
        sortOrder,
      });
      setPatrons(result.data);
      setTotal(result.total);
    } catch {
      setPatrons([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, patronTypeFilter, branchFilter, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    if (!hasPermission) return;
    loadPatrons();
  }, [hasPermission, loadPatrons]);

  const handleSort = (key: string) => {
    const field = key as "joinedAt" | "email" | "name";
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const openEditPatron = useCallback((patron: PatronListItem) => {
    setEditPatron(patron);
    setEditPatronTypeId(patron.patronType?.id ?? "");
    setEditHomeBranchId(patron.homeBranch?.id ?? "");
  }, []);

  const handleSavePatronEdit = async () => {
    if (!editPatron) return;
    setEditSaving(true);
    try {
      await patronsApi.updateMembership(editPatron.membershipId, {
        patronTypeId: editPatronTypeId || null,
        homeBranchId: editHomeBranchId || null,
      });
      toast.success("Patron updated successfully");
      setEditPatron(null);
      loadPatrons();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update patron");
    } finally {
      setEditSaving(false);
    }
  };

  const patronName = (p: PatronListItem) => formatPersonName(p.firstName, p.lastName, "—");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilters = searchQuery || patronTypeFilter !== "all" || branchFilter !== "all";

  /* ── Column definitions ───────────────────────────────── */

  const columns = useMemo<DataTableColumn<PatronListItem>[]>(
    () => [
      {
        id: "patron",
        header: "Patron",
        sortKey: "name",
        cell: (p) => (
          <div>
            <p className="font-medium text-slate-900">{patronName(p)}</p>
            {p.phone && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                <Phone className="w-3 h-3" />
                {p.phone}
              </p>
            )}
          </div>
        ),
      },
      {
        id: "email",
        header: "Email",
        sortKey: "email",
        cell: (p) => (
          <div className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate text-slate-600">{p.email}</span>
            {p.emailVerified && (
              <span className="text-xs text-emerald-600" title="Verified">
                ✓
              </span>
            )}
          </div>
        ),
        cellClassName: "max-w-[240px]",
      },
      {
        id: "patronType",
        header: "Patron Type",
        cell: (p) =>
          p.patronType?.name ? (
            <span className="text-slate-600">{p.patronType.name}</span>
          ) : hasEditPermission ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openEditPatron(p);
              }}
              className="font-medium text-amber-600 hover:text-amber-700 hover:underline"
            >
              Assign type
            </button>
          ) : (
            <span className="text-amber-600">No type</span>
          ),
        hideOnMobile: true,
      },
      {
        id: "branch",
        header: "Branch",
        cell: (p) =>
          p.homeBranch ? (
            <span className="flex items-center gap-1 text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400" />
              {p.homeBranch.name}
            </span>
          ) : (
            <span className="text-slate-400">—</span>
          ),
        hideOnMobile: true,
      },
      {
        id: "card",
        header: "Card",
        cell: (p) =>
          p.libraryCardNumber ? (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-slate-600">{p.libraryCardNumber}</span>
              {p.libraryCardStatus && <CardStatusBadge status={p.libraryCardStatus} />}
            </div>
          ) : (
            <span className="text-xs text-slate-400">No card</span>
          ),
        hideOnMobile: true,
      },
      {
        id: "joined",
        header: "Joined",
        sortKey: "joinedAt",
        cell: (p) => <span className="text-slate-500">{formatDate(p.joinedAt)}</span>,
        hideOnMobile: true,
      },
      {
        id: "actions",
        header: "Actions",
        headerAlign: "right",
        align: "right",
        width: "w-[72px]",
        cell: (p) => (
          <PatronRowActionsMenu
            patron={p}
            hasEditPermission={hasEditPermission}
            onEdit={() => openEditPatron(p)}
            onManage={() => navigate(`/patrons/${p.userId}`)}
            onDesk={() => navigate("/circulation", { state: { patronLookup: p.email } })}
          />
        ),
      },
    ],
    [hasEditPermission, navigate, openEditPatron]
  );

  /* ── Mobile card ──────────────────────────────────────── */

  const mobileCard = (p: PatronListItem, idx: number) => (
    <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
      <div className="flex items-start justify-between gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium tabular-nums text-slate-400">
              {(currentPage - 1) * PAGE_SIZE + idx + 1}.
            </span>
            <p className="text-sm font-semibold font-heading text-slate-900">{patronName(p)}</p>
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">{p.email}</p>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {p.patronType?.name ? (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
              {p.patronType.name}
            </span>
          ) : (
            <span className="text-xs font-medium text-amber-600">No type</span>
          )}
          <PatronRowActionsMenu
            patron={p}
            hasEditPermission={hasEditPermission}
            onEdit={() => openEditPatron(p)}
            onManage={() => navigate(`/patrons/${p.userId}`)}
            onDesk={() => navigate("/circulation", { state: { patronLookup: p.email } })}
          />
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-sm">
        <div className="flex items-center gap-3">
          {p.homeBranch && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              {p.homeBranch.name}
            </span>
          )}
          {p.libraryCardNumber && p.libraryCardStatus && (
            <CardStatusBadge status={p.libraryCardStatus} />
          )}
        </div>
        <span className="text-xs text-slate-400">{formatDate(p.joinedAt)}</span>
      </div>
    </div>
  );

  /* ── Permission denied ─────────────────────────────────── */

  if (!hasPermission) {
    return (
      <EmptyState
        icon={<ShieldOff className="w-6 h-6 text-red-400" />}
        title="Access Denied"
        description="You need permission to view the patron list. Contact your administrator."
      />
    );
  }

  /* ── Render ────────────────────────────────────────────── */

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Patrons"
        description="View and manage library patron accounts"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Patrons" }]}
        searchQuery={searchQuery}
        onSearchChange={(v) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search by name, email, or phone…"
        actions={
          <Button
            variant="outline"
            className="w-full min-h-11 touch-manipulation sm:min-h-10 sm:w-auto"
            onClick={() => navigate("/circulation")}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Circulation Desk
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={<Users className="w-5 h-5" />} label="Total Patrons" value={total} />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select
          value={patronTypeFilter}
          onValueChange={(v) => {
            setPatronTypeFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-[180px]">
            <SelectValue placeholder="Patron Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Patron Types</SelectItem>
            {patronTypes
              .filter((pt) => pt.isActive)
              .map((pt) => (
                <SelectItem key={pt.id} value={pt.id}>
                  {pt.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <Select
          value={branchFilter}
          onValueChange={(v) => {
            setBranchFilter(v);
            setCurrentPage(1);
          }}
        >
          <SelectTrigger className="min-h-11 w-full touch-manipulation sm:min-h-9 sm:w-[180px]">
            <SelectValue placeholder="Home Branch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {branches.map((b: Branch) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
                {b.isMain ? " — Main" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Assign-type banner */}
      {!loading && patrons.some((p) => !p.patronType) && hasEditPermission && (
        <div className="px-4 py-3 text-sm border rounded-lg border-amber-200 bg-amber-50 text-slate-900">
          <strong>Some patrons lack a patron type</strong> — they cannot borrow until you assign
          one. Click <strong>Edit</strong> to update.
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={patrons}
        rowKey={(p) => p.id}
        loading={loading}
        showSerialNumber
        serialNumberOffset={(currentPage - 1) * PAGE_SIZE}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        mobileCard={mobileCard}
        emptyState={
          <EmptyState
            icon={<Users className="w-6 h-6 text-slate-400" />}
            title={hasActiveFilters ? "No patrons match your filters" : "No patrons yet"}
            description={
              hasActiveFilters
                ? "Try adjusting your search or filters."
                : "Patrons will appear here once they register and are approved."
            }
            action={
              hasActiveFilters ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery("");
                    setPatronTypeFilter("all");
                    setBranchFilter("all");
                    setCurrentPage(1);
                  }}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        }
        pagination={
          total > PAGE_SIZE ? (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          ) : undefined
        }
      />

      {/* ── Edit Patron Dialog ────────────────────────────── */}
      <Dialog open={!!editPatron} onOpenChange={(open) => !open && setEditPatron(null)}>
        <DialogContent className="flex max-h-[min(90vh,600px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0 border-slate-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Edit2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold font-heading text-slate-900">
                  Edit Patron
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-slate-500">
                  Update patron type and home branch.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {editPatron && (
            <div className="flex-1 px-6 py-5 space-y-5 overflow-y-auto">
              <div>
                <p className="text-sm font-medium text-slate-900">{patronName(editPatron)}</p>
                <p className="text-sm text-slate-500">{editPatron.email}</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Patron Type</Label>
                <Select value={editPatronTypeId} onValueChange={setEditPatronTypeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select patron type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {patronTypes
                      .filter((pt) => pt.isActive)
                      .map((pt) => (
                        <SelectItem key={pt.id} value={pt.id}>
                          {pt.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  Required for checkout. Determines borrow limits and loan periods.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Home Branch</Label>
                <Select
                  value={editHomeBranchId || "__none__"}
                  onValueChange={(v) => setEditHomeBranchId(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select home branch…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {branches.map((b: Branch) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                        {b.isMain ? " — Main" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/60 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="w-full min-h-11 touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={() => setEditPatron(null)}
            >
              Cancel
            </Button>
            <Button
              className="w-full min-h-11 touch-manipulation sm:min-h-10 sm:w-auto"
              onClick={handleSavePatronEdit}
              disabled={editSaving || !editPatronTypeId}
            >
              {editSaving ? <LoadingSpinner /> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default PatronsListPage;
